import { Component, NgZone, OnDestroy, OnInit } from '@angular/core';

import { Title } from '@angular/platform-browser';

import { TranslocoService } from '@jsverse/transloco';
import { Subscription } from 'rxjs';
import { SearchBox_onChangeOverrideEvent } from '@inmoment/the-kitchen';

import { AppService } from 'app/app.service';
import { NavbarService } from 'app/services/navbar.service';
import {
  injectComponentTelemetryService,
  EnableComponentTelemetry,
  EnableClassTelemetry,
} from 'app/services/otel.service';
import { HasTitle } from '../../../has-title';
import { AuthService, Session } from '../../../providers/bridge-api/auth.service';
import { Group, GroupService } from '../../../providers/semantria-api/group.service';
import { GroupCreateDialogOptions } from '../components/group-create-dialog/group-create-dialog.component';
import { groupNameValidatorFactory } from '../../configuration/configuration-utils';

@EnableClassTelemetry()
@Component({
  selector: 'app-group-list',
  templateUrl: './group-list.component.html',
  styleUrls: ['./group-list.component.scss'],
  standalone: false,
})
export class GroupListComponent extends HasTitle implements OnInit, OnDestroy {
  @EnableComponentTelemetry() otelService = injectComponentTelemetryService();
  private session: Session;

  isLoading = true;

  groups: Group[] = [];
  //Groups filtered by search
  filteredGroups: Group[] = [];
  selectedGroup: Group;

  showGroupCreateModal = false;
  groupCreateData: GroupCreateDialogOptions;
  private translationSubscription: Subscription;
  constructor(
    protected titleService: Title,
    private groupService: GroupService,
    private authService: AuthService,
    private appService: AppService,
    private navbarService: NavbarService,
    private translocoService: TranslocoService,
    private ngZone: NgZone
  ) {
    super(titleService);
  }

  async ngOnInit() {
    this.translationSubscription = this.translocoService
      .selectTranslateObject('group-list')
      .subscribe((translations) => {
        this.setTitle(translations['page-title']);
        this.internalProps.searchBox.placeholder = translations['group-search'];
      });
    const [session, groups] = await Promise.all([
      this.authService.getSession(),
      this.groupService.getGroups(),
    ]);
    this.session = session;
    this.groups = groups;

    this.groups.sort((g1, g2) => g1.group_name.localeCompare(g2.group_name));

    this.filteredGroups = this.groups;
    if (this.groups.length) {
      this.selectedGroup = this.filteredGroups[0];
    }
    this.isLoading = false;
  }

  ngOnDestroy() {
    this.translationSubscription?.unsubscribe();
  }
  canCreateGroup(): boolean {
    return !this.authService.isReadOnly;
  }

  onCreateGroup() {
    this.showGroupCreateModal = true;
    this.groupCreateData = {
      nameValidator: groupNameValidatorFactory(this.groups),
      accountId: this.session.user.account_id,
    };
  }

  async onGroupCreateModalClosed(res) {
    this.showGroupCreateModal = false;
    if (res) {
      this.isLoading = true;
      await this.groupService.refreshAndSelectActiveGroup(undefined, true);
      this.selectedGroup = res;
      this.groups.push(res);
      this.groups.sort((g1, g2) => g1.group_name.localeCompare(g2.group_name));
      this.refreshGroupList();
    }
  }

  onGroupSelected(group) {
    this.selectedGroup = group;
  }

  async onGroupDeleted(group: Group) {
    this.isLoading = true;
    await this.groupService.refreshAndSelectActiveGroup(undefined, true);
    this.groups = this.groups.filter((g) => g.group_id !== group.group_id);
    this.refreshGroupList();
    const nextGroupIndex = this.filteredGroups.findIndex((g) => g.group_id === group.group_id);
    if (this.filteredGroups.length) {
      if (nextGroupIndex === -1 || !this.filteredGroups[nextGroupIndex]) {
        this.selectedGroup = this.filteredGroups[0];
      } else {
        this.selectedGroup = this.filteredGroups[nextGroupIndex];
      }
    } else {
      this.selectedGroup = undefined;
    }
    this.navbarService.rebuildNavbar();
  }

  public internalProps = {
    searchBox: {
      placeholder: '',
      onChangeOverride: ($event: SearchBox_onChangeOverrideEvent) => {
        this.ngZone.run(() => {
          this.filteredGroups = this.groups.filter(
            (s) =>
              s.group_name.toLocaleLowerCase().indexOf($event.searchText?.toLocaleLowerCase()) >= 0
          );
        });
      },
    },
  };

  async onGroupChanged() {
    await this.groupService.refreshAndSelectActiveGroup(undefined, true);
  }

  private refreshGroupList() {
    this.filteredGroups = this.groups;
    this.isLoading = false;
  }
}
